import React, { useState } from "react";
import { submitEnquiry, uploadPrivate, formatApiError } from "@/lib/api";
import { GoldButton } from "@/components/common";
import { toast } from "sonner";
import { CheckCircle, Paperclip, Spinner } from "@phosphor-icons/react";

const Field = ({ label, name, type = "text", required, value, onChange, placeholder }) => (
  <div>
    <label className="mb-1.5 block text-[13px] font-medium text-navy/80">{label}{required && <span className="text-gold-dark"> *</span>}</label>
    <input
      data-testid={`field-${name}`}
      type={type} name={name} required={required} value={value || ""} onChange={onChange} placeholder={placeholder}
      className="w-full rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm text-navy outline-none transition-colors focus:border-gold focus:ring-1 focus:ring-gold"
    />
  </div>
);

const Area = ({ label, name, required, value, onChange, placeholder }) => (
  <div className="md:col-span-2">
    <label className="mb-1.5 block text-[13px] font-medium text-navy/80">{label}{required && <span className="text-gold-dark"> *</span>}</label>
    <textarea
      data-testid={`field-${name}`}
      name={name} required={required} value={value || ""} onChange={onChange} placeholder={placeholder} rows={4}
      className="w-full rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm text-navy outline-none transition-colors focus:border-gold focus:ring-1 focus:ring-gold"
    />
  </div>
);

/**
 * fields: array of {name,label,type?,required?,area?,options?}
 */
export default function EnquiryForm({ type, fields, allowUpload = false, submitLabel = "Submit Enquiry" }) {
  const [form, setForm] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadPrivate(file);
      setAttachments((a) => [...a, { id: res.id, filename: res.filename }]);
      toast.success("Document uploaded securely");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await submitEnquiry({ type, ...form, attachments });
      setDone(res.reference);
      toast.success("Your enquiry has been submitted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="enquiry-success" className="rounded-lg border border-slate-200 bg-softgrey-100 p-10 text-center">
        <CheckCircle size={48} weight="fill" className="mx-auto text-gold-dark" />
        <h3 className="mt-4 font-serif text-3xl text-navy">Thank you</h3>
        <p className="mx-auto mt-3 max-w-md text-sm text-softgrey-600">
          Your enquiry has been received. Reference <strong>{done}</strong>. A member of the team will review it and get back to you.
          A confirmation email has been sent to you.
        </p>
      </div>
    );
  }

  return (
    <form data-testid={`enquiry-form-${type}`} onSubmit={onSubmit} className="grid grid-cols-1 gap-5 md:grid-cols-2">
      {fields.map((f) =>
        f.area ? (
          <Area key={f.name} {...f} value={form[f.name]} onChange={onChange} />
        ) : f.options ? (
          <div key={f.name}>
            <label className="mb-1.5 block text-[13px] font-medium text-navy/80">{f.label}{f.required && <span className="text-gold-dark"> *</span>}</label>
            <select data-testid={`field-${f.name}`} name={f.name} required={f.required} value={form[f.name] || ""} onChange={onChange}
              className="w-full rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm text-navy outline-none focus:border-gold focus:ring-1 focus:ring-gold">
              <option value="">Select…</option>
              {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        ) : (
          <Field key={f.name} {...f} value={form[f.name]} onChange={onChange} />
        )
      )}

      {allowUpload && (
        <div className="md:col-span-2">
          <label className="mb-1.5 block text-[13px] font-medium text-navy/80">Upload medical documents (confidential)</label>
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 px-4 py-3 text-sm text-softgrey-600 transition-colors hover:border-gold">
            {uploading ? <Spinner size={18} className="animate-spin" /> : <Paperclip size={18} />}
            <span>{uploading ? "Uploading…" : "Attach a document (PDF, image; max 25MB)"}</span>
            <input data-testid="field-attachment" type="file" className="hidden" onChange={onFile} accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" />
          </label>
          {attachments.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs text-navy/70">
              {attachments.map((a) => <li key={a.id}>✓ {a.filename}</li>)}
            </ul>
          )}
          <p className="mt-2 text-xs text-softgrey-400">Documents are stored securely and are never publicly accessible.</p>
        </div>
      )}

      <div className="md:col-span-2 pt-2">
        <GoldButton data-testid="enquiry-submit" type="submit" disabled={submitting}>
          {submitting ? "Submitting…" : submitLabel}
        </GoldButton>
      </div>
    </form>
  );
}
